
import { Profile } from "../profile/types";
import { useToast } from "../use-toast";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { UseMutateFunction, useQueryClient } from "@tanstack/react-query";

export const useGymActions = (
  profile: Profile,
  updateProfile: UseMutateFunction<Profile, Error, Partial<Profile>, unknown>
) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const joinGym = async (code: string) => {
    if (!code.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Por favor ingresa un código de gimnasio",
      });
      return false;
    }

    if (code.length !== 6) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "El código debe tener exactamente 6 caracteres",
      });
      return false;
    }

    setIsLoading(true);
    try {
      const { data: gym, error } = await supabase
        .from("gyms")
        .select("id, name")
        .eq("code", code.toLowerCase())
        .single();

      if (error) throw error;
      if (!gym) throw new Error("Código de gimnasio inválido");

      // Verificar si ya está inscrito en este gimnasio
      const { data: existingMembership } = await supabase
        .from("user_gym_memberships")
        .select("id, is_active")
        .eq("user_id", profile.id)
        .eq("gym_id", gym.id)
        .single();

      if (existingMembership) {
        if (existingMembership.is_active) {
          toast({
            variant: "destructive",
            title: "Ya estás inscrito",
            description: `Ya eres miembro de ${gym.name}`,
          });
          return false;
        } else {
          // Reactivar membresía existente
          await supabase
            .from("user_gym_memberships")
            .update({ is_active: true, updated_at: new Date().toISOString() })
            .eq("id", existingMembership.id);
        }
      } else {
        // Crear nueva membresía
        await supabase
          .from("user_gym_memberships")
          .insert({
            user_id: profile.id,
            gym_id: gym.id,
            is_active: true
          });
      }

      // Si es el primer gimnasio del usuario, establecerlo como gym_id principal
      if (!profile.gym_id) {
        updateProfile({ gym_id: gym.id });
      }

      // Invalidate gym-related queries to ensure fresh data
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["userGyms", profile.id] }),
        queryClient.invalidateQueries({ queryKey: ["gym", gym.id] }),
        queryClient.invalidateQueries({ queryKey: ["profile"] }),
        queryClient.invalidateQueries({ queryKey: ["classes"] }),
      ]);

      toast({
        title: "¡Éxito!",
        description: `Te has unido a ${gym.name}`,
      });
      
      return true;
    } catch (error: any) {
      console.error("Error joining gym:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "No se pudo unir al gimnasio",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const leaveGym = async (gymId?: string) => {
    setIsLoading(true);
    try {
      const targetGymId = gymId || profile.gym_id;
      
      if (!targetGymId) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "No se especificó qué gimnasio abandonar",
        });
        return false;
      }

      // Desactivar la membresía específica
      const { error } = await supabase
        .from("user_gym_memberships")
        .update({ is_active: false })
        .eq("user_id", profile.id)
        .eq("gym_id", targetGymId);

      if (error) throw error;

      // Si es el gimnasio principal, buscar otro gimnasio para establecer como principal
      if (targetGymId === profile.gym_id) {
        const { data: otherGyms } = await supabase
          .from("user_gym_memberships")
          .select("gym_id")
          .eq("user_id", profile.id)
          .eq("is_active", true)
          .neq("gym_id", targetGymId)
          .limit(1);

        const newPrimaryGymId = otherGyms && otherGyms.length > 0 ? otherGyms[0].gym_id : null;
        updateProfile({ gym_id: newPrimaryGymId });
      }
      
      // Invalidate gym-related queries to ensure fresh data
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["userGyms", profile.id] }),
        queryClient.invalidateQueries({ queryKey: ["gym", targetGymId] }),
        queryClient.invalidateQueries({ queryKey: ["profile"] }),
        queryClient.invalidateQueries({ queryKey: ["classes"] }),
      ]);
      
      toast({
        title: "¡Éxito!",
        description: "Has abandonado el gimnasio",
      });
      
      return true;
    } catch (error) {
      console.error("Error leaving gym:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo abandonar el gimnasio",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    joinGym,
    leaveGym,
  };
};
